import { supabase } from '@/integrations/supabase/client';
import { api } from '@/api/api-client';
import {
  forgotPassword as authForgotPassword,
  requestVerificationCode as authRequestVerificationCode,
  resetPassword as authResetPassword,
  AuthResult,
  signIn as authSignIn,
  signOut as authSignOut,
  signUp as authSignUp,
  verifyOTP as authVerifyOTP,
  getUserDashboardRoute,
  SignUpData,
  signInWithGoogle as authSignInWithGoogle,
  handleGoogleCallback as authHandleGoogleCallback,
  signInWithApple as authSignInWithApple,
  handleAppleCallback as authHandleAppleCallback,
  UserType,
} from '@/services/auth/auth';
import { Session, User } from '@supabase/supabase-js';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { clearSupabaseAuthToken } from '@/utils/supabaseAuth';

type ErrorWithMessage = {
  message: string;
};

function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Record<string, unknown>).message === 'string'
  );
}

// Define a type for the verification result
interface VerificationResult {
  success: boolean;
  session?: Session | null;
  user?: User | null;
  error?: string;
}

interface AuthContextProps {
  session: Session | null;
  user: User | null;
  userType: string | null;
  signIn: (email: string, password: string) => Promise<AuthResult>;
  signUp: (
    email: string,
    password: string,
    userData: Record<string, unknown>
  ) => Promise<AuthResult>;
  signOut: () => Promise<void>;
  isLoading: boolean;
  forgotPassword: (email: string) => Promise<AuthResult>;
  resetPassword: (password: string) => Promise<AuthResult>;
  checkProfileSetup: () => Promise<boolean>;
  checkFilmmakerProfileSetup: () => Promise<boolean>;
  checkClientProfileSetup: () => Promise<boolean>;
  checkDashboardAccess: (
    dashboardType: 'filmmaker' | 'client'
  ) => Promise<boolean>;
  isVerified: boolean;
  checkVerificationStatus: () => Promise<boolean>;
  checkRoleVerificationStatus: (
    role: 'filmmaker' | 'client'
  ) => Promise<boolean>;
  verifyOTP: (email: string, token: string) => Promise<VerificationResult>;
  requestVerificationCode: (email: string) => Promise<boolean>;
  signInWithGoogle: (userType?: UserType) => Promise<AuthResult>;
  handleGoogleCallback: () => Promise<AuthResult>;
  signInWithApple: (userType?: UserType) => Promise<AuthResult>;
  handleAppleCallback: () => Promise<AuthResult>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextProps>({
  session: null,
  user: null,
  userType: null,
  signIn: async () => ({ success: false }),
  signUp: async () => ({ success: false }),
  signOut: async () => {},
  isLoading: true,
  forgotPassword: async () => ({ success: false }),
  resetPassword: async () => ({ success: false }),
  checkProfileSetup: async () => false,
  checkFilmmakerProfileSetup: async () => false,
  checkClientProfileSetup: async () => false,
  checkDashboardAccess: async () => false,
  isVerified: false,
  checkVerificationStatus: async () => false,
  checkRoleVerificationStatus: async () => false,
  verifyOTP: async () => ({ success: false }),
  requestVerificationCode: async () => false,
  signInWithGoogle: async () => ({ success: false }),
  handleGoogleCallback: async () => ({ success: false }),
  signInWithApple: async () => ({ success: false }),
  handleAppleCallback: async () => ({ success: false }),
  refreshSession: async () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [userType, setUserType] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const navigate = useNavigate();

  const checkVerificationStatus = async (): Promise<boolean> => {
    if (!user || !userType) return false;

    try {
      if (userType === 'filmmaker' || userType === 'both') {
        const response = await api<any[]>(`/filmmakers?user_id=${user.id}`);
        const filmmakerData = response[0];

        if (filmmakerData && filmmakerData.verified === true) {
          setIsVerified(true);
          return true;
        }

        // Skip verification prompt for claimed profiles (they can verify later via banner)
        if (filmmakerData && filmmakerData.skip_verification_prompt === true) {
          // Don't set isVerified - they can still see the verification banner
          return true;
        }
      }

      if (userType === 'client' || userType === 'both') {
        const response = await api<any[]>(`/clients?user_id=${user.id}`);
        const clientData = response[0];

        if (clientData && clientData.verified === true) {
          setIsVerified(true);
          return true;
        }
      }

      setIsVerified(false);
      return false;
    } catch (error) {
      console.error('Verification check error:', error);
      setIsVerified(false);
      return false;
    }
  };

  // New function to check verification for a specific role
  const checkRoleVerificationStatus = async (
    role: 'filmmaker' | 'client'
  ): Promise<boolean> => {
    if (!user) return false;

    try {
      const endpoint = role === 'filmmaker' ? '/filmmakers' : '/clients';
      const response = await api<any[]>(`${endpoint}?user_id=${user.id}`);
      const roleData = response[0];

      if (roleData && roleData.verified === true) {
        return true;
      }

      // Skip verification prompt for claimed profiles (filmmaker only)
      if (role === 'filmmaker' && roleData && roleData.skip_verification_prompt === true) {
        return true;
      }

      // Dual users: one verification covers both sides. If the OTHER role is
      // verified, treat this role as verified too.
      if (userType === 'both') {
        const otherEndpoint = role === 'filmmaker' ? '/clients' : '/filmmakers';
        const otherResponse = await api<any[]>(`${otherEndpoint}?user_id=${user.id}`);
        if (otherResponse?.[0]?.verified === true) return true;
      }

      return false;
    } catch (error) {
      console.error(`${role} verification check error:`, error);
      return false;
    }
  };

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, currentSession) => {
      if (event === 'SIGNED_OUT' || !currentSession) {
        setSession(null);
        setUser(null);
        setUserType(null);
        setIsVerified(false);
        clearSupabaseAuthToken();

        const currentPath = window.location.pathname;
        if (
          currentPath.includes('/dashboard') ||
          currentPath.includes('/profile-setup') ||
          currentPath.includes('/verify-identity')
        ) {
          window.location.href = '/';
        }
        return;
      }

      setSession(currentSession);
      setUser(currentSession?.user ?? null);

      if (currentSession?.user) {
        const type = currentSession.user.user_metadata?.user_type;
        setUserType(type);

        if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
          setTimeout(() => {
            checkVerificationStatus();
          }, 0);
        }
      }
    });

    const checkSession = async () => {
      try {
        const {
          data: { session: initialSession },
        } = await supabase.auth.getSession();

        if (!initialSession) {
          setSession(null);
          setUser(null);
          setUserType(null);
          setIsVerified(false);
          clearSupabaseAuthToken();

          const currentPath = window.location.pathname;
          if (
            currentPath.includes('/dashboard') ||
            currentPath.includes('/profile-setup') ||
            currentPath.includes('/verify-identity')
          ) {
            navigate('/login', { replace: true });
          }
        } else {
          setSession(initialSession);
          setUser(initialSession?.user ?? null);

          if (initialSession?.user) {
            const type = initialSession.user.user_metadata?.user_type;
            setUserType(type);

            setTimeout(() => {
              checkVerificationStatus();
            }, 0);
          }
        }

        setIsLoading(false);
      } catch (error) {
        console.error('Session check error:', error);
        setSession(null);
        setUser(null);
        setUserType(null);
        setIsVerified(false);
        setIsLoading(false);
        clearSupabaseAuthToken();

        const currentPath = window.location.pathname;
        if (
          currentPath.includes('/dashboard') ||
          currentPath.includes('/profile-setup') ||
          currentPath.includes('/verify-identity')
        ) {
          navigate('/login', { replace: true });
        }
      }
    };

    checkSession();

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  const redirectToSetupPage = (type: string | null) => {
    if (type === 'filmmaker') {
      navigate('/filmmaker/profile-setup/basic');
    } else if (type === 'client') {
      navigate('/client/profile-setup/basic');
    } else if (type === 'both') {
      navigate('/filmmaker/profile-setup/basic');
    }
  };

  // Check if filmmaker profile is set up
  const checkFilmmakerProfileSetup = async (): Promise<boolean> => {
    if (!user) return false;

    try {
      const response = await api<any[]>(`/filmmakers?user_id=${user.id}`);
      const filmmakerData = response[0];

      if (!filmmakerData) {
        return false;
      }

      return filmmakerData?.setup === true;
    } catch (error) {
      console.error('Filmmaker profile setup check error:', error);
      return false;
    }
  };

  // Check if client profile is set up
  const checkClientProfileSetup = async (): Promise<boolean> => {
    if (!user) return false;

    try {
      const response = await api<any[]>(`/clients?user_id=${user.id}`);
      const clientData = response[0];

      if (!clientData) {
        return false;
      }

      return clientData?.setup === true;
    } catch (error) {
      console.error('Client profile setup check error:', error);
      return false;
    }
  };

  // Check if the specific dashboard type is accessible for dual accounts
  const checkDashboardAccess = async (
    dashboardType: 'filmmaker' | 'client'
  ): Promise<boolean> => {
    if (!user || !userType) return false;

    // For single account types, use the standard profile setup check
    if (userType !== 'both') {
      return checkProfileSetup();
    }

    // For dual accounts, check the specific profile setup
    if (dashboardType === 'filmmaker') {
      return checkFilmmakerProfileSetup();
    } else if (dashboardType === 'client') {
      return checkClientProfileSetup();
    }

    return false;
  };

  const checkProfileSetup = async (): Promise<boolean> => {
    if (!user || !userType) return false;

    try {
      let isProfileSetup = false;

      if (userType === 'filmmaker' || userType === 'both') {
        const response = await api<any[]>(`/filmmakers?user_id=${user.id}`);
        const filmmakerData = response[0];

        if (filmmakerData && filmmakerData.setup === true) {
          isProfileSetup = true;
        }
      }

      if ((userType === 'client' || userType === 'both') && !isProfileSetup) {
        const response = await api<any[]>(`/clients?user_id=${user.id}`);
        const clientData = response[0];

        if (clientData && clientData.setup === true) {
          isProfileSetup = true;
        }
      }

      return isProfileSetup;
    } catch (error) {
      console.error('Profile setup check error:', error);
      return false;
    }
  };

  const signIn = async (
    email: string,
    password: string
  ): Promise<AuthResult> => {
    try {
      const { success, data, error, isGoogleAccount } = await authSignIn(email, password);

      if (success && data.session) {
        setSession(data.session);
        setUser(data.session.user);

        const type = data.session.user.user_metadata?.user_type;
        setUserType(type);

        // Defer navigation decisions to the onAuthStateChange listener & ProtectedRoute.
        toast.success('Login successful');

        return { success: true };
      } else {
        // Show specific error message for Google accounts
        if (isGoogleAccount) {
          toast.error(error || 'This email is associated with a Google account. Please sign in with Google instead.', {
            duration: 5000,
          });
        } else if (error && (error.toLowerCase().includes('banned') || error.toLowerCase().includes('ban'))) {
          toast.error('This account has been deleted. If you believe this is an error, please contact support at info@findmyfilmmaker.com', { duration: 8000 });
        } else {
          toast.error(error || 'Please check your credentials and try again');
        }
        return { success: false, error };
      }
    } catch (error: unknown) {
      const errorMessage = isErrorWithMessage(error)
        ? error.message
        : 'An unexpected error occurred';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const signUp = async (
    email: string,
    password: string,
    userData: Record<string, unknown>
  ): Promise<AuthResult> => {
    try {
      const result = await authSignUp({
        email,
        password,
        userData: userData as SignUpData['userData'],
      });

      if (result.success) {
        toast.info('Verification Required', {
          description: 'Check your email for a verification code',
        });

        return result;
      } else {
        // Show specific error messages based on the error type
        if (result.isExistingAccount && result.isGoogleAccount) {
          toast.error(result.error || 'An account with this email already exists using Google sign-in. Please sign in with Google instead.', {
            duration: 5000,
          });
        } else if (result.isExistingAccount) {
          toast.error(result.error || 'An account with this email already exists. Please sign in instead.', {
            duration: 5000,
          });
        } else {
          toast.error(result.error || 'Please try again');
        }
        return result;
      }
    } catch (error: unknown) {
      const errorMessage = isErrorWithMessage(error)
        ? error.message
        : 'An unexpected error occurred';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      const { success, error } = await authSignOut();

      if (success) {
        setSession(null);
        setUser(null);
        setUserType(null);
        setIsVerified(false);
        clearSupabaseAuthToken();

        window.location.href = '/login';
      } else {
        setSession(null);
        setUser(null);
        setUserType(null);
        setIsVerified(false);

        if (error) {
          if (!error.includes('Auth session missing')) {
            toast.error(error || 'An error occurred during sign out');
          }
        }

        window.location.href = '/login';
      }
    } catch (error: unknown) {
      console.error('Sign out error:', error);

      setSession(null);
      setUser(null);
      setUserType(null);
      setIsVerified(false);

      toast.error(
        "There was a problem during sign out, but you've been logged out successfully"
      );

      window.location.href = '/login';
    }
  };

  const forgotPassword = async (email: string): Promise<AuthResult> => {
    try {
      const { success, error } = await authForgotPassword(email);

      if (success) {
        toast.success('Reset Link Sent', {
          description: 'Check your email for a password reset link',
        });
        return { success: true };
      } else {
        toast.error(error || 'Failed to send reset link');
        return { success: false, error };
      }
    } catch (error: unknown) {
      const errorMessage = isErrorWithMessage(error)
        ? error.message
        : 'An unexpected error occurred';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const resetPassword = async (password: string): Promise<AuthResult> => {
    try {
      const { success, error } = await authResetPassword(password);

      if (success) {
        toast.success('Password Reset', {
          description: 'Your password has been reset successfully',
        });
        return { success: true };
      } else {
        toast.error(error || 'Failed to reset password');
        return { success: false, error };
      }
    } catch (error: unknown) {
      const errorMessage = isErrorWithMessage(error)
        ? error.message
        : 'An unexpected error occurred';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const verifyOTP = async (
    email: string,
    token: string
  ): Promise<VerificationResult> => {
    try {
      const result = await authVerifyOTP(email, token);

      if (result) {
        toast.success('Account verified!', {
          description: 'Your account has been verified successfully.',
        });

        if (result.session) {
          setSession(result.session);
          setUser(result.user);

          if (result.user) {
            const type = result.user.user_metadata?.user_type;
            setUserType(type);

            setTimeout(() => {
              checkVerificationStatus();
            }, 0);

            // Send welcome email after successful verification
            try {
              const { emailAPI } = await import('@/services/email/email-api');
              const userData = result.user.user_metadata;

              await emailAPI.sendWelcomeEmail(result.user.email || '', {
                firstName: userData?.first_name || 'there',
                userType: type || 'user',
              });
            } catch (emailError) {
              console.error('Failed to send welcome email:', emailError);
              // Don't fail the verification process if email fails
            }
          }
        }

        return {
          success: true,
          session: result.session,
          user: result.user,
        };
      } else {
        toast.error('Verification failed', {
          description: 'Please check your verification code and try again',
        });
        return { success: false };
      }
    } catch (error: unknown) {
      const errorMessage = isErrorWithMessage(error)
        ? error.message
        : 'An unexpected error occurred';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const requestVerificationCode = async (email: string): Promise<boolean> => {
    try {
      const success = await authRequestVerificationCode(email);

      if (success) {
        toast.success('Code sent', {
          description: 'A verification code has been sent to your email',
        });
        return true;
      } else {
        toast.error('Failed to send verification code');
        return false;
      }
    } catch (error: unknown) {
      const errorMessage = isErrorWithMessage(error)
        ? error.message
        : 'Failed to send verification code';
      toast.error(errorMessage);
      return false;
    }
  };

  const signInWithGoogle = async (userType?: UserType): Promise<AuthResult> => {
    try {
      const result = await authSignInWithGoogle(userType);

      if (result.success) {
        return { success: true };
      } else {
        toast.error(result.error || 'Google sign-in failed');
        return { success: false, error: result.error };
      }
    } catch (error: unknown) {
      const errorMessage = isErrorWithMessage(error)
        ? error.message
        : 'An unexpected error occurred';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const handleGoogleCallback = async (): Promise<AuthResult> => {
    try {
      const result = await authHandleGoogleCallback();

      if (result.success) {
        // Handle successful result with proper typing
        if (result.session) {
          setSession(result.session);
        }
        if (result.user) {
          setUser(result.user);

          const type = result.userType || result.user.user_metadata?.user_type;
          setUserType(type);

          setTimeout(() => {
            checkVerificationStatus();
          }, 0);
        }

        return result;
      } else {
        // Don't show toast for email_conflict - let the component handle it
        if (result.error !== 'email_conflict') {
          toast.error(result.error || 'Google callback handling failed');
        }

        return result; // Return the result as-is, including email conflict data
      }
    } catch (error: unknown) {
      console.error('AuthContext: Error in handleGoogleCallback:', error);
      const errorMessage = isErrorWithMessage(error)
        ? error.message
        : 'An unexpected error occurred';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const signInWithApple = async (userType?: UserType): Promise<AuthResult> => {
    try {
      const result = await authSignInWithApple(userType);

      if (result.success) {
        return { success: true };
      } else {
        toast.error(result.error || 'Apple sign-in failed');
        return { success: false, error: result.error };
      }
    } catch (error: unknown) {
      const errorMessage = isErrorWithMessage(error)
        ? error.message
        : 'An unexpected error occurred';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const handleAppleCallback = async (): Promise<AuthResult> => {
    try {
      const result = await authHandleAppleCallback();

      if (result.success) {
        // Handle successful result with proper typing
        if (result.session) {
          setSession(result.session);
        }
        if (result.user) {
          setUser(result.user);

          const type = result.userType || result.user.user_metadata?.user_type;
          setUserType(type);

          setTimeout(() => {
            checkVerificationStatus();
          }, 0);
        }

        return result;
      } else {
        // Don't show toast for email_conflict - let the component handle it
        if (result.error !== 'email_conflict') {
          toast.error(result.error || 'Apple callback handling failed');
        }

        return result; // Return the result as-is, including email conflict data
      }
    } catch (error: unknown) {
      console.error('AuthContext: Error in handleAppleCallback:', error);
      const errorMessage = isErrorWithMessage(error)
        ? error.message
        : 'An unexpected error occurred';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const refreshSession = async (): Promise<void> => {
    try {
      const {
        data: { session: refreshedSession },
      } = await supabase.auth.getSession();

      if (refreshedSession) {
        setSession(refreshedSession);
        setUser(refreshedSession.user);

        if (refreshedSession.user) {
          const type = refreshedSession.user.user_metadata?.user_type;
          setUserType(type);

          setTimeout(() => {
            checkVerificationStatus();
          }, 0);
        }
      }
    } catch (error) {
      console.error('Session refresh error:', error);
      setSession(null);
      setUser(null);
      setUserType(null);
      setIsVerified(false);
    }
  };

  const value = {
    session,
    user,
    userType,
    signIn,
    signUp,
    signOut,
    isLoading,
    forgotPassword,
    resetPassword,
    checkProfileSetup,
    checkFilmmakerProfileSetup,
    checkClientProfileSetup,
    checkDashboardAccess,
    isVerified,
    checkVerificationStatus,
    checkRoleVerificationStatus,
    verifyOTP,
    requestVerificationCode,
    signInWithGoogle,
    handleGoogleCallback,
    signInWithApple,
    handleAppleCallback,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
